import { useEffect, useState, Fragment } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Handshake,
    Award,
    Wallet,
    Building2,
    Loader2,
    ArrowLeft,
    QrCode,
    Smartphone,
    Car,
    Sparkles,
    CheckCircle2,
    XCircle,
    ShieldCheck,
    ShieldOff,
    ExternalLink,
    Clock,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";

/**
 * Admin → Vermittler-Management.
 *
 * Three tabs:
 *  1. Vermittler-Liste mit Punktestand + Inline-Punktevergabe
 *  2. Offene Auszahlungs-Anfragen → paid/credited/rejected
 *  3. Offene Upgrade-Anträge → approved/rejected (flippt role auf b2b)
 */
export default function AdminVermittlersPage() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("list");

    useEffect(() => {
        if (!authLoading && user?.role !== "admin") {
            navigate("/");
        }
    }, [user, authLoading, navigate]);

    if (authLoading || !user || user.role !== "admin") {
        return (
            <div className="flex items-center justify-center min-h-[40vh]">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto px-4 py-8 space-y-6" data-testid="admin-vermittlers-page">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Handshake className="w-7 h-7 text-[#FFCC00]" />
                    <div>
                        <h1 className="font-display text-2xl font-bold">Vermittler-Verwaltung</h1>
                        <p className="text-sm text-gray-600">Punkte vergeben, Auszahlungen freigeben, Upgrade-Anträge prüfen.</p>
                    </div>
                </div>
                <Link to="/admin/dealers" className="inline-flex items-center gap-2 text-sm font-bold text-gray-700 hover:text-[#111827]">
                    <ArrowLeft className="w-4 h-4" /> Admin-Übersicht
                </Link>
            </div>

            <div className="flex border-b border-gray-200 gap-1" data-testid="admin-vermittler-tabs">
                <TabBtn active={tab === "list"} onClick={() => setTab("list")} testid="tab-list">Vermittler</TabBtn>
                <TabBtn active={tab === "payouts"} onClick={() => setTab("payouts")} testid="tab-payouts">Auszahlungen</TabBtn>
                <TabBtn active={tab === "upgrades"} onClick={() => setTab("upgrades")} testid="tab-upgrades">Upgrade-Anträge</TabBtn>
                <TabBtn active={tab === "id-verifications"} onClick={() => setTab("id-verifications")} testid="tab-id-verifications">ID-Prüfungen</TabBtn>
            </div>

            {tab === "list" && <VermittlerList />}
            {tab === "payouts" && <PayoutQueue />}
            {tab === "upgrades" && <UpgradeQueue />}
            {tab === "id-verifications" && <IdVerificationQueue />}
        </div>
    );
}


function TabBtn({ active, onClick, children, testid }) {
    return (
        <button
            onClick={onClick}
            className={`px-4 py-2 text-sm font-bold uppercase tracking-wider border-b-2 transition-colors ${active ? "border-[#FFCC00] text-[#111827]" : "border-transparent text-gray-500 hover:text-[#111827]"}`}
            data-testid={testid}
        >
            {children}
        </button>
    );
}


function VermittlerList() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);
    const { impersonate } = useAuth();
    const navigate = useNavigate();
    const doImpersonate = async (id) => {
        try { await impersonate(id); toast.success("In Konto gewechselt"); navigate("/dashboard"); }
        catch (e) { toast.error(e.response?.data?.detail || "Wechsel fehlgeschlagen"); }
    };

    const load = async () => {
        try {
            const { data } = await api.get("/admin/vermittlers");
            setItems(data.items || []);
        } catch (e) {
            toast.error("Vermittler konnten nicht geladen werden");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    if (loading) return <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div>;
    if (items.length === 0) return <div className="bg-white border border-gray-200 rounded-sm p-8 text-center text-sm text-gray-500" data-testid="vermittlers-empty">Noch keine Vermittler registriert.</div>;

    return (
        <div className="bg-white border border-gray-200 rounded-sm">
            <table className="w-full text-sm" data-testid="vermittlers-table">
                <thead className="bg-gray-50 border-b border-gray-200 text-[10px] uppercase tracking-wider text-gray-600">
                    <tr>
                        <th className="text-left px-4 py-3">Name / E-Mail</th>
                        <th className="text-right px-4 py-3">Erhalten</th>
                        <th className="text-right px-4 py-3">Verfügbar</th>
                        <th className="text-right px-4 py-3">Ausgezahlt</th>
                        <th className="text-left px-4 py-3">Upgrade?</th>
                        <th className="text-right px-4 py-3">Aktion</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((v) => (
                        <Fragment key={v.id}>
                            <tr className="border-b border-gray-100" data-testid="vermittler-row">
                                <td className="px-4 py-3">
                                    <div className="font-bold text-[#111827]">{v.name || "—"}</div>
                                    <div className="text-[11px] text-gray-500 font-mono-num">{v.email}</div>
                                </td>
                                <td className="px-4 py-3 text-right font-mono-num font-bold text-amber-700">{v.points_awarded}</td>
                                <td className="px-4 py-3 text-right font-mono-num font-bold text-green-700">{v.points_available}</td>
                                <td className="px-4 py-3 text-right font-mono-num text-blue-700">{v.points_paid_out}</td>
                                <td className="px-4 py-3">
                                    {v.upgrade_to_b2b_requested && !v.upgrade_to_b2b_decided_at && (
                                        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px] font-bold">Pending</Badge>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <div className="inline-flex items-center gap-2">
                                        <Button
                                            size="sm"
                                            onClick={() => doImpersonate(v.id)}
                                            className="rounded-sm text-xs font-bold uppercase tracking-wider bg-[#B45309] hover:bg-[#92400E] text-white"
                                            data-testid={`impersonate-${v.id}`}
                                            title="Als dieser Vermittler anmelden (Admin-Modus)"
                                        >
                                            In Konto wechseln
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => setExpanded(expanded === v.id ? null : v.id)}
                                            className="rounded-sm text-xs"
                                            data-testid="award-points-toggle"
                                        >
                                            {expanded === v.id ? "Schließen" : "Punkte vergeben"}
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                            {expanded === v.id && (
                                <tr>
                                    <td colSpan={6} className="bg-amber-50/40 border-b border-gray-100 px-4 py-4">
                                        <AwardForm vid={v.id} onDone={() => { load(); setExpanded(null); }} />
                                    </td>
                                </tr>
                            )}
                        </Fragment>
                    ))}
                </tbody>
            </table>
        </div>
    );
}


const CAT_OPTIONS = [
    { value: "qr_smilie", label: "QR-Code Smilie", icon: QrCode },
    { value: "nfc", label: "NFC-Aktion", icon: Smartphone },
    { value: "referral_vehicle", label: "Fahrzeug-Vermittlung", icon: Car },
    { value: "other", label: "Sonstige Aktion", icon: Sparkles },
];

function AwardForm({ vid, onDone }) {
    const [category, setCategory] = useState("qr_smilie");
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [points, setPoints] = useState("");
    const [referenceId, setReferenceId] = useState("");
    const [working, setWorking] = useState(false);

    const submit = async () => {
        const n = Number(points);
        if (!title.trim()) { toast.error("Titel erforderlich"); return; }
        if (!Number.isInteger(n) || n <= 0) { toast.error("Punkte > 0 erforderlich"); return; }
        setWorking(true);
        try {
            await api.post(`/admin/vermittler/${vid}/activities`, {
                category, title: title.trim(),
                description: description.trim() || null,
                points: n,
                reference_id: referenceId.trim() || null,
            });
            toast.success(`${n} Punkte vergeben`);
            setTitle(""); setDescription(""); setPoints(""); setReferenceId("");
            onDone();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Vergabe fehlgeschlagen");
        } finally {
            setWorking(false);
        }
    };

    return (
        <div className="space-y-3" data-testid={`award-form-${vid}`}>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Kategorie</Label>
                    <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full h-10 rounded-sm border border-gray-200 mt-1 px-2 text-sm" data-testid="award-category">
                        {CAT_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                </div>
                <div className="sm:col-span-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Titel</Label>
                    <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="z.B. Smilie auf Tankstelle München-Pasing" className="rounded-sm mt-1" data-testid="award-title" />
                </div>
                <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Punkte</Label>
                    <Input type="number" min="1" value={points} onChange={(e) => setPoints(e.target.value)} className="rounded-sm mt-1 font-mono-num" data-testid="award-points" />
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Beschreibung (optional)</Label>
                    <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="rounded-sm mt-1" data-testid="award-description" />
                </div>
                <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Referenz-ID (optional)</Label>
                    <Input value={referenceId} onChange={(e) => setReferenceId(e.target.value)} placeholder="listing-id, Order-#" className="rounded-sm mt-1 font-mono-num" data-testid="award-reference" />
                </div>
            </div>
            <Button onClick={submit} disabled={working} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm uppercase tracking-wider" data-testid="award-submit">
                <Award className="w-4 h-4 mr-2" />
                {working ? "Vergebe…" : "Punkte vergeben"}
            </Button>
        </div>
    );
}


function PayoutQueue() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/vermittler/payouts", { params: { status_filter: "pending" } });
            setItems(data.items || []);
        } catch (e) {
            toast.error("Auszahlungen konnten nicht geladen werden");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const decide = async (id, decision) => {
        try {
            await api.post(`/admin/vermittler/payouts/${id}/decide`, { decision });
            toast.success(`Markiert als ${decision}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Entscheidung fehlgeschlagen");
        }
    };

    if (loading) return <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div>;
    if (items.length === 0) return <div className="bg-white border border-gray-200 rounded-sm p-8 text-center text-sm text-gray-500" data-testid="payouts-empty">Keine offenen Auszahlungen.</div>;

    return (
        <div className="space-y-3">
            {items.map((p) => (
                <div key={p.id} className="bg-white border border-gray-200 rounded-sm p-4 flex flex-wrap items-start gap-4" data-testid="payout-row">
                    <div className="flex-1 min-w-[200px]">
                        <div className="flex items-center gap-2">
                            <Wallet className="w-4 h-4 text-[#FFCC00]" />
                            <span className="font-bold text-[#111827]">{p.vermittler?.name || "—"}</span>
                            <span className="text-[11px] text-gray-500 font-mono-num">{p.vermittler?.email}</span>
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                            <strong className="text-xl font-mono-num text-amber-700">{p.points}</strong> Punkte ·{" "}
                            Methode: <strong>{p.method}</strong>
                        </div>
                        {p.method === "bank" && p.vermittler?.bank_iban && (
                            <div className="text-[10px] text-gray-500 font-mono-num mt-1">IBAN: {p.vermittler.bank_iban}</div>
                        )}
                        {p.method === "paypal" && p.vermittler?.payout_paypal_email && (
                            <div className="text-[10px] text-gray-500 font-mono-num mt-1">PayPal: {p.vermittler.payout_paypal_email}</div>
                        )}
                        {p.note && <div className="text-xs text-gray-600 italic mt-1">„{p.note}"</div>}
                        <div className="text-[10px] text-gray-400 mt-1">{new Date(p.created_at).toLocaleString("de-DE")}</div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button size="sm" onClick={() => decide(p.id, "paid")} className="bg-green-600 hover:bg-green-700 text-white font-bold rounded-sm text-xs" data-testid="payout-mark-paid">
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Ausgezahlt
                        </Button>
                        <Button size="sm" onClick={() => decide(p.id, "credited")} variant="outline" className="rounded-sm text-xs" data-testid="payout-mark-credited">
                            Verrechnet
                        </Button>
                        <Button size="sm" onClick={() => decide(p.id, "rejected")} variant="outline" className="rounded-sm text-xs text-red-700 border-red-300 hover:bg-red-50" data-testid="payout-mark-rejected">
                            <XCircle className="w-3.5 h-3.5 mr-1" /> Ablehnen
                        </Button>
                    </div>
                </div>
            ))}
        </div>
    );
}


function UpgradeQueue() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/vermittler/upgrades");
            setItems(data.items || []);
        } catch (e) {
            toast.error("Anträge konnten nicht geladen werden");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const decide = async (vid, decision) => {
        if (decision === "approved" && !window.confirm("Diesen Vermittler zum B2B-Händler aufwerten? Die Rolle wird sofort gewechselt.")) return;
        try {
            await api.post(`/admin/vermittler/${vid}/upgrade/decide`, { decision });
            toast.success(`Antrag ${decision}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Entscheidung fehlgeschlagen");
        }
    };

    if (loading) return <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div>;
    if (items.length === 0) return <div className="bg-white border border-gray-200 rounded-sm p-8 text-center text-sm text-gray-500" data-testid="upgrades-empty">Keine offenen Upgrade-Anträge.</div>;

    return (
        <div className="space-y-3">
            {items.map((v) => (
                <div key={v.id} className="bg-white border border-gray-200 rounded-sm p-4 flex flex-wrap items-start gap-4" data-testid="upgrade-row">
                    <div className="flex-1 min-w-[200px]">
                        <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-[#FFCC00]" />
                            <span className="font-bold text-[#111827]">{v.name}</span>
                            <span className="text-[11px] text-gray-500 font-mono-num">{v.email}</span>
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                            Antrag eingereicht: {v.upgrade_to_b2b_requested_at ? new Date(v.upgrade_to_b2b_requested_at).toLocaleString("de-DE") : "—"}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                            Stammdaten: {[v.street, v.postal_code, v.city].filter(Boolean).join(", ") || "unvollständig"}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                            ID-Nachweis: {v.id_card_front_url ? "Ausweis ✓" : v.passport_url ? "Pass ✓" : "—"}
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button size="sm" onClick={() => decide(v.id, "approved")} className="bg-green-600 hover:bg-green-700 text-white font-bold rounded-sm text-xs" data-testid="upgrade-approve">
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Genehmigen → B2B
                        </Button>
                        <Button size="sm" onClick={() => decide(v.id, "rejected")} variant="outline" className="rounded-sm text-xs text-red-700 border-red-300 hover:bg-red-50" data-testid="upgrade-reject">
                            <XCircle className="w-3.5 h-3.5 mr-1" /> Ablehnen
                        </Button>
                    </div>
                </div>
            ))}
        </div>
    );
}



function IdVerificationQueue() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("pending");
    const [working, setWorking] = useState(null);
    const [notes, setNotes] = useState({});

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/vermittler/id-verifications", {
                params: { status_filter: statusFilter },
            });
            setItems(data.items || []);
        } catch (e) {
            toast.error("Liste konnte nicht geladen werden");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, [statusFilter]);

    const decide = async (vid, decision) => {
        if (decision === "rejected" && !notes[vid]?.trim()) {
            toast.error("Bei Ablehnung bitte eine Notiz angeben (Grund für den Vermittler).");
            return;
        }
        if (!window.confirm(decision === "approved"
            ? "Identität wirklich GENEHMIGEN?"
            : "Identitäts-Prüfung wirklich ABLEHNEN?")) return;
        setWorking(vid);
        try {
            await api.post(`/admin/vermittler/${vid}/verify-id`, {
                decision,
                note: notes[vid] || null,
            });
            toast.success(decision === "approved" ? "Identität genehmigt" : "Anfrage abgelehnt");
            setNotes((p) => ({ ...p, [vid]: "" }));
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
        } finally {
            setWorking(null);
        }
    };

    const STATUS_META = {
        pending: { label: "In Prüfung", Icon: Clock, color: "bg-amber-100 text-amber-800 border-amber-300" },
        approved: { label: "Genehmigt", Icon: ShieldCheck, color: "bg-green-100 text-green-800 border-green-300" },
        rejected: { label: "Abgelehnt", Icon: ShieldOff, color: "bg-red-100 text-red-800 border-red-300" },
    };

    return (
        <div className="space-y-3" data-testid="id-verification-queue">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
                {["pending", "approved", "rejected", "all"].map((s) => (
                    <button
                        key={s}
                        onClick={() => setStatusFilter(s)}
                        className={`px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-sm border transition-colors ${statusFilter === s ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-300 hover:border-[#111827]"}`}
                        data-testid={`id-verif-filter-${s}`}
                    >
                        {s === "pending" ? "In Prüfung" : s === "approved" ? "Genehmigt" : s === "rejected" ? "Abgelehnt" : "Alle"}
                    </button>
                ))}
                <Button onClick={load} variant="outline" size="sm" className="ml-auto rounded-sm text-xs" data-testid="id-verif-reload">
                    Neu laden
                </Button>
            </div>

            {loading ? (
                <div className="p-8 text-center text-gray-400" data-testid="id-verif-loading">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                </div>
            ) : items.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-sm p-8 text-center text-sm text-gray-500" data-testid="id-verif-empty">
                    Keine Vermittler in dieser Kategorie.
                </div>
            ) : items.map((v) => {
                const meta = STATUS_META[v.id_verification_status] || STATUS_META.pending;
                const Icon = meta.Icon;
                const docs = [
                    ["Ausweis vorn", v.id_card_front_url],
                    ["Ausweis hinten", v.id_card_back_url],
                    ["Reisepass", v.passport_url],
                    ["Meldebescheinigung", v.residence_certificate_url],
                ].filter(([, url]) => !!url);
                return (
                    <div key={v.id} className="bg-white border border-gray-200 rounded-sm p-4" data-testid={`id-verif-row-${v.id}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Badge className={`uppercase tracking-wide font-bold rounded-sm border ${meta.color}`}>
                                        <Icon className="w-3 h-3 mr-1" /> {meta.label}
                                    </Badge>
                                    <span className="text-sm font-bold">{v.name || "—"}</span>
                                    <span className="text-xs text-gray-500 font-mono-num">{v.email}</span>
                                </div>
                                <div className="mt-2 text-xs text-gray-600">
                                    {v.city ? `${v.city} · ` : ""}
                                    {v.tax_id ? <>Steuer-ID: <span className="font-mono-num">{v.tax_id}</span></> : "—"}
                                </div>
                                {v.id_verification_requested_at && (
                                    <div className="mt-1 text-[10px] text-gray-400 font-mono-num">
                                        Beantragt: {new Date(v.id_verification_requested_at).toLocaleString("de-DE")}
                                        {v.id_verification_decided_at && ` · Bearbeitet: ${new Date(v.id_verification_decided_at).toLocaleString("de-DE")}`}
                                    </div>
                                )}
                                {v.id_verification_note && (
                                    <div className="mt-2 text-xs text-gray-700 italic" data-testid={`id-verif-note-${v.id}`}>
                                        Letzte Notiz: „{v.id_verification_note}"
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-2" data-testid={`id-verif-docs-${v.id}`}>
                            {docs.length === 0 ? (
                                <span className="text-xs text-amber-700">⚠ Keine Dokumente hochgeladen</span>
                            ) : docs.map(([label, url]) => (
                                <a
                                    key={label}
                                    href={url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-200 rounded-sm text-xs text-blue-800 font-bold hover:bg-blue-100"
                                >
                                    {label} <ExternalLink className="w-3 h-3" />
                                </a>
                            ))}
                        </div>

                        {v.id_verification_status === "pending" && (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">
                                    Notiz an Vermittler (bei Ablehnung Pflicht)
                                </Label>
                                <Textarea
                                    value={notes[v.id] || ""}
                                    onChange={(e) => setNotes((p) => ({ ...p, [v.id]: e.target.value }))}
                                    placeholder='z. B. "Ausweis-Rückseite unscharf — bitte erneut hochladen"'
                                    rows={2}
                                    maxLength={500}
                                    className="rounded-sm mt-1 text-xs"
                                    data-testid={`id-verif-note-input-${v.id}`}
                                />
                                <div className="flex flex-wrap gap-2 mt-2">
                                    <Button
                                        size="sm"
                                        onClick={() => decide(v.id, "approved")}
                                        disabled={working === v.id}
                                        className="bg-green-600 hover:bg-green-700 text-white font-bold rounded-sm text-xs"
                                        data-testid={`id-verif-approve-${v.id}`}
                                    >
                                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Genehmigen
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={() => decide(v.id, "rejected")}
                                        disabled={working === v.id}
                                        variant="outline"
                                        className="rounded-sm text-xs text-red-700 border-red-300 hover:bg-red-50"
                                        data-testid={`id-verif-reject-${v.id}`}
                                    >
                                        <XCircle className="w-3.5 h-3.5 mr-1" /> Ablehnen
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
